/**
 * useSubscription Hook
 *
 * Fetches current organization's subscription from entitlement service.
 * Provides plan, features, limits, usage data, and credit balance.
 *
 * This hook retrieves real-time subscription information from the backend,
 * ensuring the frontend always reflects the user's actual plan and limits.
 *
 * Usage:
 *   const { subscription, loading, error, reload } = useSubscription();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *
 *   const isPro = subscription?.plan === 'pro';
 *   const canGenerate = subscription?.features?.media_generation;
 *   const campaignLimit = subscription?.usage?.campaigns_per_month?.limit;
 *   const availableCredits = subscription?.credits?.available_credits;
 *   const bonusCredits = subscription?.credits?.remaining_bonus_credits;
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/api';

/** Check whether an auth token is available (without importing the full auth hook) */
function hasAuthToken(): boolean {
  return !!(localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token'));
}

/**
 * Usage limit information for a specific metric
 */
export interface UsageLimitInfo {
  limit: number;           // Maximum allowed (-1 = unlimited)
  current: number;         // Current usage count
  remaining: number;       // Remaining quota
  percentage: number;      // Usage as percentage (0-100)
  is_unlimited: boolean;   // True if limit is -1
  resets_at?: string;      // ISO timestamp when usage resets
}

/**
 * Credit balance information for media generation
 */
export interface CreditBalanceInfo {
  total_credits: number;           // Monthly credit allocation from plan
  used_credits: number;            // Credits used in current period
  remaining_credits: number;       // Monthly credits remaining
  bonus_credits: number;           // One-time purchased credits
  used_bonus_credits: number;      // Bonus credits used
  remaining_bonus_credits: number; // Bonus credits remaining
  available_credits: number;       // Total available (monthly + bonus remaining)
  percentage_used: number;         // Percentage of monthly credits used (0-100)
  period_start: string;            // ISO timestamp of current period start
  period_end: string;              // ISO timestamp when credits reset
}

/**
 * Complete subscription data from entitlement service
 */
export interface SubscriptionData {
  organization_id: string;
  plan: string;                           // Plan identifier (free, basic, pro, enterprise)
  plan_display_name: string;              // Human-readable plan name
  status: string;                         // Subscription status (active, trialing, past_due, etc.)
  is_active: boolean;                     // Whether subscription is currently active
  is_trialing: boolean;                   // Whether in trial period
  trial_ends?: string;                    // ISO timestamp when trial ends
  period_ends?: string;                   // ISO timestamp when current period ends
  features: Record<string, boolean>;      // Feature flags (media_generation, etc.)
  usage: Record<string, UsageLimitInfo>;  // Usage limits and current counts
  credits?: CreditBalanceInfo;            // Credit balance for media generation (optional for backward compatibility)
  available_tools: string[];              // List of available tool names
  supported_platforms?: string[];         // Supported advertising platforms
}

/**
 * Return type for useSubscription hook
 */
export interface UseSubscriptionReturn {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Hook for fetching and managing subscription data
 *
 * Automatically fetches subscription on mount and provides reload function.
 * Returns null subscription on error with graceful degradation.
 *
 * @returns Subscription data, loading state, error, and reload function
 */
export const useSubscription = (): UseSubscriptionReturn => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSubscription = useCallback(async (isRefresh = false) => {
    // Skip if no auth token — avoids ERR_NETWORK from unauthenticated CORS preflight
    if (!hasAuthToken()) {
      if (!isRefresh) setLoading(false);
      return;
    }

    // Only show loading spinner on initial load, not on background refreshes
    if (!isRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      // Fetch current organization's subscription from entitlement service
      const response = await apiClient.get('/api/v1/my-subscription');
      setSubscription(response.data);
    } catch (err: any) {
      console.error('Failed to load subscription:', err);

      // On refresh failures, keep existing data — don't blank out the UI
      if (isRefresh) return;

      // Set error message for display
      let errorMessage = 'Failed to load subscription';
      if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setSubscription(null);
    } finally {
      if (!isRefresh) {
        setLoading(false);
      }
    }
  }, []);

  // Load subscription on mount
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  // Reload subscription when organization changes or credits are updated
  useEffect(() => {
    const handleOrganizationChange = () => {
      loadSubscription(false);
    };
    const handleCreditsUpdated = () => {
      loadSubscription(true);
    };

    window.addEventListener('organizationChanged', handleOrganizationChange);
    window.addEventListener('creditsUpdated', handleCreditsUpdated);

    return () => {
      window.removeEventListener('organizationChanged', handleOrganizationChange);
      window.removeEventListener('creditsUpdated', handleCreditsUpdated);
    };
  }, [loadSubscription]);

  return {
    subscription,
    loading,
    error,
    reload: loadSubscription,
  };
};

export default useSubscription;
